import {
  BaseSyntheticEvent,
  ChangeEvent,
  FC,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import useGoogle from "react-google-autocomplete/lib/usePlacesAutocompleteService";
import {
  FieldAutocompleteAddressListStyled,
  FieldAutocompleteAddressStyled,
} from "./field-autocomplete-address.styled";
import FieldText from "../field-text/field-text";
import { FieldTextType } from "../field-text/field-text.types";
import { geocodeByPlaceId } from "react-places-autocomplete";
import { createGeocodedResult } from "@dashboard/utils/geocoder.utils";
import { PlatformCountry, ResponseMapType } from "@dashboard/common/types";

type FieldAutocompleteAddressProps = Omit<
  FieldTextType,
  "onChange" | "onFocus"
> & {
  country?: PlatformCountry;
  onChange?: (values: ResponseMapType) => void;
  onChangeValue?: (e: ChangeEvent) => void;
};

const FieldAutocompleteAddress: FC<FieldAutocompleteAddressProps> = ({
  value,
  country,
  disabled,
  onChange,
  onChangeValue,
  onBlur,
  ...props
}) => {
  const { placePredictions, getPlacePredictions } = useGoogle({});
  const optionsListRef = useRef<HTMLDivElement>(null);
  const [localValue, setLocalValue] = useState(value || "");
  const [showList, setShowList] = useState(false);

  const handleFocus = () => {
    if (!disabled) {
      setShowList(true);
      document.addEventListener("mousedown", handleCloseOptions, false);
    }
  };

  const handleOnChange = (evt: BaseSyntheticEvent) => {
    const text = evt.target.value as string;
    getPlacePredictions({
      input: text,
      types: ["address"],
      componentRestrictions: { country: country?.id || "" },
    });
    setLocalValue(text);
    onChangeValue && onChangeValue(evt as ChangeEvent);
  };

  const geocodeFn = async (
    latLng: google.maps.LatLng,
    place: google.maps.places.PlaceResult
  ): Promise<void> => {
    const geocodeValues = await createGeocodedResult(latLng, place);
    if (geocodeValues) {
      onChange && onChange(geocodeValues);
    }
  };

  const onClick = (place: google.maps.places.AutocompletePrediction) => {
    setLocalValue(place.description);
    geocodeByPlaceId(place.place_id)
      .then((geoPlace) => {
        if (geoPlace.length > 0) {
          geocodeFn(geoPlace[0].geometry?.location as google.maps.LatLng, {
            ...geoPlace[0],
            formatted_address: place.description,
          });
        }
      })
      .catch(() => {});
    setShowList(false);
  };

  const handleCloseOptions = useCallback(
    (event?: Event) => {
      if (!event || !optionsListRef.current?.contains(event.target as Node)) {
        setShowList(false);
        document.removeEventListener("mousedown", handleCloseOptions, false);
      }
    },
    [localValue]
  );

  useEffect(() => {
    setLocalValue(value || "");
  }, [value]);

  return (
    <FieldAutocompleteAddressStyled ref={optionsListRef}>
      <FieldText
        value={localValue}
        disabled={disabled}
        onChange={handleOnChange}
        onFocus={handleFocus}
        {...props}
      />
      {showList && placePredictions.length > 0 && localValue.length > 0 && (
        <FieldAutocompleteAddressListStyled>
          <ul className="field-dropdown-items">
            {placePredictions.map((place, index) => (
              <li
                className={`field-dropdown-item`}
                key={`autocomplete-item-${index}`}
                onClick={() => onClick(place)}
              >
                {place.description}
              </li>
            ))}
          </ul>
        </FieldAutocompleteAddressListStyled>
      )}
    </FieldAutocompleteAddressStyled>
  );
};

export default FieldAutocompleteAddress;
